import * as React from "react";
import { Platform, StyleSheet, Text } from "react-native";
import { Feather } from "@expo/vector-icons";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { SFSymbol } from "sf-symbols-typescript";
import { MenuItemProps } from "zeego/lib/typescript/menu";

import { colors } from "~/colors";

type DropdownItemConfig = {
  icon?: {
    ios: SFSymbol;
    web: React.ComponentProps<typeof Feather>["name"];
  };
  iconComponent?: React.ReactNode;
  title: React.ReactNode;
  isActive?: boolean;
} & Omit<MenuItemProps, "children">;

type Group = {
  title?: string;
  items: DropdownItemConfig[];
};
export const Dropdown = ({
  trigger,
  groups,
  align = "start",
  side = "bottom",
  sideOffset = 5,
  alignOffset,
  asChild,
}: {
  trigger: React.ReactElement;
  groups: Array<Group>;
  align?: "start" | "end";
  side?: "top" | "bottom";
  sideOffset?: number;
  alignOffset?: number;
  asChild?: boolean;
}) => {
  // modal={false} prevents a bug when using dropdown and dialog together
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild={asChild}>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          side={side}
          alignOffset={alignOffset}
          sideOffset={sideOffset}
          loop
          style={styles.content}
        >
          {groups.map((group, i) => {
            return (
              <React.Fragment key={group.title ?? "title"}>
                <DropdownMenu.Group key={group.title ?? "title"}>
                  {group.items.map((item, i) => (
                    <React.Fragment key={item.key}>
                      <DropdownMenu.Item
                        key={item.key}
                        style={styles.dropdownItem}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onSelect={item.onSelect}
                        className="flex-row items-center gap-2"
                      >
                        {item.icon && (
                          <Feather
                            name={item.icon.web}
                            size={16}
                            color={
                              item.destructive
                                ? colors.text.warning
                                : colors.text.primary
                            }
                          />
                        )}
                        {item.iconComponent && item.iconComponent}
                        {item.title && (
                          <Text
                            style={[
                              styles.text,
                              item.destructive && styles.destructive,
                            ]}
                          >
                            {item.title}
                          </Text>
                        )}
                      </DropdownMenu.Item>
                      {i !== group.items.length - 1 && (
                        <DropdownMenu.Separator style={styles.separator} />
                      )}
                    </React.Fragment>
                  ))}
                </DropdownMenu.Group>
                {i !== groups.length - 1 && (
                  <>
                    <DropdownMenu.Separator style={styles.groupSeparator} />
                    <DropdownMenu.Separator style={styles.groupSeparator} />
                  </>
                )}
              </React.Fragment>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

const styles = StyleSheet.create({
  content: {
    backgroundColor: colors.surface.tertiary,
    borderRadius: 8,
    width: 180,
    zIndex: 100,
  },

  dropdownItem: {
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 12,
    paddingRight: 12,
    flexDirection: "row",
    gap: 8,
    display: "flex",
    ...Platform.select({
      web: {
        cursor: "pointer",
      },
    }),
  },

  destructive: {
    color: colors.text.warning,
  },

  text: {
    color: "#fff",
    fontSize: 16,
  },

  group: {
    borderTopColor: colors.grey.dark[5],
    borderTopWidth: 2,
  },

  groupSeparator: {
    borderTopColor: colors.grey.dark[0],
    borderTopWidth: 2,
  },

  separator: {
    borderTopColor: colors.grey.dark[5],
    borderTopWidth: 1,
  },

  checkbox: {
    width: 24,
    height: 24,
    borderColor: colors.input.border.active,
    borderRadius: 4,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
});
